import { useState } from 'react';

import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

// Semana ISO: lunes → domingo
dayjs.extend(isoWeek);

const MAX_DAYS = 14;

/**
 * Calcula el rango por defecto: semana anterior (lunes) → semana actual (domingo).
 */
function getDefaultRange(): [Dayjs, Dayjs] {
  const start = dayjs().subtract(1, 'week').startOf('isoWeek');
  const end = dayjs().endOf('isoWeek').startOf('day');
  return [start, end];
}

const DEFAULT_RANGE = getDefaultRange();

export interface UseDashboardFiltersReturn {
  dateRange: [Dayjs, Dayjs];
  setDateRange: (range: [Dayjs, Dayjs]) => void;
  selectedCampaignIds: number[];
  setSelectedCampaignIds: (ids: number[]) => void;
  disabledDate: (current: Dayjs, info: { from?: Dayjs }) => boolean;
  isFiltered: boolean;
  filterKey: number;
  onApply: () => void;
  onReset: () => void;
}

export function useDashboardFilters(
  onApplyCallback?: (dateRange: [Dayjs, Dayjs], campaignIds: number[]) => void
): UseDashboardFiltersReturn {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(getDefaultRange);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<number[]>([]);

  // Refleja si el último filtro APLICADO difiere del default.
  // Solo cambia al hacer Apply o Reset, nunca al tocar los inputs.
  const [isFiltered, setIsFiltered] = useState(false);

  // Incrementa cada vez que se aplica o resetea — permite que hijos
  // reaccionen a un nuevo fetch sin necesidad de levantar su estado.
  const [filterKey, setFilterKey] = useState(0);

  const disabledDate = (current: Dayjs, info: { from?: Dayjs }): boolean => {
    if (info.from) {
      return Math.abs(current.diff(info.from, 'day')) >= MAX_DAYS;
    }
    return false;
  };

  const onApply = () => {
    const defaultRange = DEFAULT_RANGE;
    const filtered =
      selectedCampaignIds.length > 0 ||
      !dateRange[0].isSame(defaultRange[0], 'day') ||
      !dateRange[1].isSame(defaultRange[1], 'day');
    setIsFiltered(filtered);
    setFilterKey((k) => k + 1);
    onApplyCallback?.(dateRange, selectedCampaignIds);
  };

  const onReset = () => {
    const fresh = getDefaultRange();
    setDateRange(fresh);
    setSelectedCampaignIds([]);
    setIsFiltered(false);
    setFilterKey((k) => k + 1);
    // Pasa los valores frescos directamente — evita leer el closure obsoleto
    onApplyCallback?.(fresh, []);
  };

  return {
    dateRange,
    setDateRange,
    selectedCampaignIds,
    setSelectedCampaignIds,
    disabledDate,
    isFiltered,
    filterKey,
    onApply,
    onReset,
  };
}
